import Head from 'next/head'

import Accordion from '@totallymoney/ui/components/Accordion'
import BlobIcon from '@totallymoney/ui/components/BlobIcon'
import Box from '@totallymoney/ui/components/Box'
import Heading from '@totallymoney/ui/components/Heading'
import Row from '@totallymoney/ui/components/Row'
import Stack from '@totallymoney/ui/components/Stack'
import Text from '@totallymoney/ui/components/Text'
import theme from '@totallymoney/ui/theme'

import useAppQueryState from '@hooks/useAppQueryState'

import { consList } from './lib/consList'
import { prosList } from './lib/prosList'

const ProsAndConsSection = () => {
    const { appQueryState } = useAppQueryState()
    const category = appQueryState.categoryId!

    const pros = prosList[category]
    const cons = consList[category]

    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'ItemList',
                            name: 'What are the pros of credit cards?',
                            description:
                                'There are advantages to having credit cards, such as:',
                            itemListOrder: 'Unordered',
                            itemListElement: [
                                pros.map(({ heading, text }) => ({
                                    '@type': 'ListItem',
                                    name: heading,
                                    description: text,
                                })),
                            ],
                        }),
                    }}
                />

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'ItemList',
                            name: 'What are the cons of credit cards?',
                            description:
                                'There are disadvantages to having credit cards, such as:',
                            itemListOrder: 'Unordered',
                            itemListElement: [
                                cons.map(({ heading, text }) => ({
                                    '@type': 'ListItem',
                                    name: heading,
                                    description: text,
                                })),
                            ],
                        }),
                    }}
                />
            </Head>

            <Box
                gridColumn={['1/13', '1/13', '3/11']}
                display="grid"
                gridTemplateColumns={['1fr', '1fr', 'repeat(2, 1fr)']}
                gridGap="space30"
            >
                <Stack space="space20" display={['none', 'none', 'flex']}>
                    <Heading variant="80">Pros</Heading>

                    {pros.map(({ heading, text }, i) => (
                        <Row space="space10" key={`${heading}-${i}`}>
                            <Box>
                                <BlobIcon
                                    blobName="checkMarkGreen"
                                    title={`Pros-${i + 1}`}
                                    size={40}
                                />
                            </Box>

                            <Text color="light" variant="100">
                                <strong color={theme.textColor}>
                                    {heading}
                                </strong>{' '}
                                - {text}
                            </Text>
                        </Row>
                    ))}
                </Stack>

                <Box display={['flex', 'flex', 'none']}>
                    <Accordion
                        header={
                            <Row space="space10" alignItems="center">
                                <Box aria-hidden>
                                    <BlobIcon
                                        blobName="checkMarkGreen"
                                        title="Pros"
                                        size={40}
                                    />
                                </Box>

                                <Heading variant="40">Pros</Heading>
                            </Row>
                        }
                    >
                        <Stack space="space30">
                            {pros.map(({ heading, text }, i) => (
                                <Row space="space10" key={`${heading}-${i}`}>
                                    <Text color="light" variant="100">
                                        <strong color={theme.textColor}>
                                            {heading}
                                        </strong>{' '}
                                        - {text}
                                    </Text>
                                </Row>
                            ))}
                        </Stack>
                    </Accordion>
                </Box>

                <Stack space="space20" display={['none', 'none', 'flex']}>
                    <Heading variant="80">Cons</Heading>
                    {cons.map(({ heading, text }, i) => (
                        <Row space="space10" key={`${heading}-${i}`}>
                            <Box>
                                <BlobIcon
                                    blobName="warning"
                                    title={`Cons-${i + 1}`}
                                    size={40}
                                />
                            </Box>

                            <Text color="light" variant="100">
                                <strong color={theme.textColor}>
                                    {heading}
                                </strong>{' '}
                                - {text}
                            </Text>
                        </Row>
                    ))}
                </Stack>

                <Box display={['flex', 'flex', 'none']}>
                    <Accordion
                        header={
                            <Row space="space10" alignItems="center">
                                <Box aria-hidden>
                                    <BlobIcon
                                        blobName="warning"
                                        title="Cons"
                                        size={40}
                                    />
                                </Box>

                                <Heading variant="40">Cons</Heading>
                            </Row>
                        }
                    >
                        <Stack space="space30">
                            {cons.map(({ heading, text }, i) => (
                                <Row space="space10" key={`${heading}-${i}`}>
                                    <Text color="light" variant="100">
                                        <strong color={theme.textColor}>
                                            {heading}
                                        </strong>{' '}
                                        - {text}
                                    </Text>
                                </Row>
                            ))}
                        </Stack>
                    </Accordion>
                </Box>
            </Box>
        </>
    )
}

export default ProsAndConsSection
